import type { Dispatch, SetStateAction } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import type { CartLine } from "@/types/order";
import { cartLineKey } from "@/helpers/cartLineKey";
import { cartItemCount, cartSubtotalGrosze } from "@/helpers/cartPricing";
import { generateId } from "@/helpers/generateId";

export type CartStatus = "idle" | "submitting" | "error";

/** Exactly what `partialize` writes to localStorage. */
interface PersistedCart {
  restaurantId: string | null;
  tableId: string | null;
  lines: CartLine[];
}

interface CartState {
  /** The scope the cart belongs to — a cart is never valid across tables. */
  restaurantId: string | null;
  tableId: string | null;
  lines: CartLine[];
  tipGrosze: number;
  /**
   * Whether the guest has answered the tip question, which is not the same as
   * a tip above zero: picking 0% is an answer, and must not be overwritten by
   * the default the next time the step opens.
   */
  tipChosen: boolean;
  paymentMethod: string | null;
  blikCode: string;
  status: CartStatus;

  ensureScope: (restaurantId: string, tableId: string) => void;
  addLine: (line: Omit<CartLine, "lineId">) => void;
  setQuantity: (lineId: string, quantity: number) => void;
  removeLine: (lineId: string) => void;
  setTipGrosze: (tipGrosze: number) => void;
  setPaymentMethod: (paymentMethod: string | null) => void;
  /** Accepts the updater form, because OtpInput drives it like useState. */
  setBlikCode: Dispatch<SetStateAction<string>>;
  setStatus: (status: CartStatus) => void;
  clear: () => void;
}

const emptyCart = {
  lines: [] as CartLine[],
  tipGrosze: 0,
  tipChosen: false,
  paymentMethod: null as string | null,
  blikCode: "",
  status: "idle" as CartStatus,
};

/**
 * The cart lives in its own store, deliberately.
 *
 * Every consumer of useVenuesDataStore subscribes to the whole store with no
 * selector — including the app-wrapping BackdropStateProvider — so routing a
 * quantity stepper through it would re-render the entire tree on every tap.
 * Read this one through the selector hooks below instead.
 */
export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      restaurantId: null,
      tableId: null,
      ...emptyCart,

      ensureScope: (restaurantId, tableId) => {
        const state = get();
        if (state.restaurantId === restaurantId && state.tableId === tableId) {
          return;
        }
        // A cart restored from a different table (or restaurant) would order
        // the wrong food to the wrong place. Drop it.
        set({ restaurantId, tableId, ...emptyCart });
      },

      addLine: (line) =>
        set((state) => {
          const key = cartLineKey(line);
          const existing = state.lines.find(
            (candidate) => cartLineKey(candidate) === key,
          );

          if (!existing) {
            return { lines: [...state.lines, { ...line, lineId: generateId() }] };
          }

          return {
            lines: state.lines.map((candidate) =>
              candidate.lineId === existing.lineId
                ? { ...candidate, quantity: candidate.quantity + line.quantity }
                : candidate,
            ),
          };
        }),

      setQuantity: (lineId, quantity) =>
        set((state) => ({
          lines:
            quantity <= 0
              ? state.lines.filter((line) => line.lineId !== lineId)
              : state.lines.map((line) =>
                  line.lineId === lineId ? { ...line, quantity } : line,
                ),
        })),

      removeLine: (lineId) =>
        set((state) => ({
          lines: state.lines.filter((line) => line.lineId !== lineId),
        })),

      setTipGrosze: (tipGrosze) => set({ tipGrosze, tipChosen: true }),
      setPaymentMethod: (paymentMethod) => set({ paymentMethod }),
      setBlikCode: (blikCode) =>
        set((state) => ({
          blikCode:
            typeof blikCode === "function" ? blikCode(state.blikCode) : blikCode,
        })),
      setStatus: (status) => set({ status }),
      clear: () => set({ ...emptyCart }),
    }),
    {
      name: "order-cart-store",
      version: 1,
      // Only the cart itself survives a reload. Tip, payment method and status
      // are checkout scratch space — restoring a stale tip, or a "submitting"
      // status, into a fresh session would be worse than starting over.
      partialize: (state) => ({
        restaurantId: state.restaurantId,
        tableId: state.tableId,
        lines: state.lines,
      }),
      // Discard anything written by an older, unknown cart shape rather than
      // letting it crash the flow.
      migrate: (persisted, version) =>
        version === 1 ? (persisted as PersistedCart) : undefined,
    },
  ),
);

export const useCartLines = () => useCartStore((state) => state.lines);
export const useCartStatus = () => useCartStore((state) => state.status);
export const useCartTipGrosze = () => useCartStore((state) => state.tipGrosze);
export const useCartTipChosen = () =>
  useCartStore((state) => state.tipChosen);
export const useCartPaymentMethod = () =>
  useCartStore((state) => state.paymentMethod);
export const useCartBlikCode = () => useCartStore((state) => state.blikCode);

// Both derive a primitive, so zustand v5's identity check is satisfied without
// memoization.
export const useCartItemCount = () =>
  useCartStore((state) => cartItemCount(state.lines));
export const useCartSubtotal = () =>
  useCartStore((state) => cartSubtotalGrosze(state.lines));

export const useCartActions = () =>
  useCartStore(
    useShallow((state) => ({
      ensureScope: state.ensureScope,
      addLine: state.addLine,
      setQuantity: state.setQuantity,
      removeLine: state.removeLine,
      setTipGrosze: state.setTipGrosze,
      setPaymentMethod: state.setPaymentMethod,
      setBlikCode: state.setBlikCode,
      setStatus: state.setStatus,
      clear: state.clear,
    })),
  );
